import React, { useMemo, memo, useId, useState, useCallback, useEffect, useRef } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
  CartesianGrid,
  usePlotArea,
} from 'recharts';
import { format } from 'date-fns';
import { getChartThemeColors } from '../../utils/chartColors';
import { ELO_POLICY_V2_EFFECTIVE_AT } from '../../utils/eloUtils';

const CHART_MARGIN = { top: 12, right: 12, left: 0, bottom: 8 };
const FINE_HIT_DISTANCE = 28;
const COARSE_HIT_DISTANCE = 48;

function getTeamPlayersFromGame(sessionGame, teamNumber) {
  return (sessionGame?.players_session_games || [])
    .filter((row) => row.team_number === teamNumber)
    .map((row) => row.players)
    .filter(Boolean);
}

function formatPlayerShortName(player) {
  if (!player) return '?';
  const first = (player.first_name || '').trim();
  const last = (player.last_name || '').trim();
  if (!first && !last) return '?';
  if (!last) return first;
  return `${first} ${last.charAt(0).toUpperCase()}.`;
}

function formatTeamShortNames(players) {
  if (!players?.length) return '?';
  return players.map((player) => formatPlayerShortName(player)).join('+');
}

function formatGameMatchup(sessionGame) {
  if (!sessionGame) return null;
  const team1 = formatTeamShortNames(getTeamPlayersFromGame(sessionGame, 1));
  const team2 = formatTeamShortNames(getTeamPlayersFromGame(sessionGame, 2));
  if (team1 === '?' && team2 === '?') return null;
  return `${team1} vs ${team2}`;
}

function formatGameScore(sessionGame) {
  if (!sessionGame) return null;
  const team1Score = sessionGame.team_1_score;
  const team2Score = sessionGame.team_2_score;
  if (team1Score == null && team2Score == null) return null;
  return `${team1Score ?? 0}-${team2Score ?? 0}`;
}

function normalizeLabel(value) {
  return (value || '').trim().replace(/\s+/g, ' ');
}

function stripClubSuffixFromSession(sessionName, clubName) {
  const session = normalizeLabel(sessionName);
  const club = normalizeLabel(clubName);
  if (!session || !club) return session;

  const suffixes = [` - ${club}`, ` – ${club}`, ` at ${club}`];
  for (const suffix of suffixes) {
    if (session.endsWith(suffix)) {
      return session.slice(0, -suffix.length).trim();
    }
  }
  return session;
}

function sessionIncludesClub(sessionName, clubName) {
  const session = normalizeLabel(sessionName).toLowerCase();
  const club = normalizeLabel(clubName).toLowerCase();
  if (!session || !club || club === 'unknown club') return false;
  if (session === club) return true;
  return session.endsWith(` - ${club}`) || session.endsWith(` – ${club}`) || session.endsWith(` at ${club}`);
}

function buildTooltipContextLine(sessionName, clubName) {
  const club = normalizeLabel(clubName);
  const session = stripClubSuffixFromSession(sessionName, clubName) || 'Unknown Session';
  if (!club || club === 'Unknown Club' || sessionIncludesClub(sessionName, club)) {
    return session;
  }
  return `${session} · ${club}`;
}

/** Pure transform — keep out of render path without memo. */
function buildChartDataFromEloHistory(eloHistory) {
  if (!eloHistory?.length) return [];

  const sortedHistory = [...eloHistory].sort((a, b) => {
    const dateA = new Date(a.session_games?.created_at || a.created_at);
    const dateB = new Date(b.session_games?.created_at || b.created_at);
    return dateA - dateB;
  });

  const chartData = [];
  let mixedGameCount = 0;
  let levelsGameCount = 0;

  sortedHistory.forEach((record) => {
    const date = record.session_games?.created_at || record.created_at;
    const gameDate = new Date(date);
    const formattedDate = format(gameDate, 'yyyy-MM-dd');
    const dateLabel = format(gameDate, 'd MMM yyyy');

    let gameCount = null;
    if (record.pair_type === 'mixed') {
      mixedGameCount += 1;
      gameCount = mixedGameCount;
    } else if (record.pair_type === 'levels') {
      levelsGameCount += 1;
      gameCount = levelsGameCount;
    }
    if (gameCount === null) return;

    const sessionGame = record.session_games;
    const dataPoint = {
      gameCount,
      createdAt: date,
      date: formattedDate,
      dateLabel,
      clubName: record.clubs?.name || 'Unknown Club',
      sessionName: record.sessions?.name || 'Unknown Session',
      gameMatchup: formatGameMatchup(sessionGame),
      gameScore: formatGameScore(sessionGame),
      sessionGameId: record.session_game_id,
    };

    if (record.pair_type === 'mixed') {
      dataPoint.mixedElo = record.new_value;
      dataPoint.mixedChange = record.elo_change || 0;
      dataPoint.mixedOldValue = record.old_value;
    } else if (record.pair_type === 'levels') {
      dataPoint.levelsElo = record.new_value;
      dataPoint.levelsChange = record.elo_change || 0;
      dataPoint.levelsOldValue = record.old_value;
    }

    chartData.push(dataPoint);
  });

  return chartData;
}

/**
 * Vertical marker at each ladder's first game on/after the v2 Elo policy.
 * Only drawn when that ladder also has a game from before the change.
 */
function getEloPolicyUpdateMarkers(chartData, { showMixed, showLevels }) {
  const cutoff = new Date(ELO_POLICY_V2_EFFECTIVE_AT).getTime();
  const both = showMixed && showLevels;
  const markers = [];

  const findForSeries = (eloKey, seriesId, bothLabel) => {
    const points = (chartData || []).filter((point) => point[eloKey] != null);
    let hasBefore = false;
    let firstAfterCount = null;
    for (const point of points) {
      const ms = new Date(point.createdAt).getTime();
      if (Number.isNaN(ms)) continue;
      if (ms < cutoff) {
        hasBefore = true;
        continue;
      }
      if (firstAfterCount == null) firstAfterCount = point.gameCount;
    }
    if (!hasBefore || firstAfterCount == null) return;
    markers.push({
      gameCount: firstAfterCount,
      seriesId,
      label: both ? bothLabel : 'Rating update',
    });
  };

  if (showMixed) findForSeries('mixedElo', 'mixed', 'Mixed update');
  if (showLevels) findForSeries('levelsElo', 'levels', 'Levels update');

  const byCount = new Map();
  markers.forEach((marker) => {
    const existing = byCount.get(marker.gameCount);
    if (!existing) {
      byCount.set(marker.gameCount, marker);
      return;
    }
    byCount.set(marker.gameCount, { ...existing, label: 'Rating update' });
  });
  return [...byCount.values()];
}

function getSeriesPoints(chartData, eloKey) {
  return chartData
    .filter((point) => point[eloKey] != null)
    .sort((a, b) => a.gameCount - b.gameCount);
}

function pointToPixel(point, eloKey, plot, minY, maxY, maxGameCount) {
  const xMin = 1;
  const xMax = maxGameCount || 1;
  const xRange = xMax - xMin;
  const yRange = maxY - minY || 1;
  return {
    px: xRange === 0
      ? plot.x + plot.width / 2
      : plot.x + ((point.gameCount - xMin) / xRange) * plot.width,
    py: plot.y + (1 - (point[eloKey] - minY) / yRange) * plot.height,
  };
}

function distanceToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return { dist: Math.hypot(px - x1, py - y1), t: 0 };
  }
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lengthSquared));
  return { dist: Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy)), t };
}

function pickNearestSeries({
  chartX,
  chartY,
  seriesList,
  plot,
  minY,
  maxY,
  maxGameCount,
  maxDistance,
}) {
  if (!plot?.width || !plot?.height) return null;

  let best = null;

  seriesList.forEach((series) => {
    if (!series.points.length) return;

    const pixels = series.points.map((point) => {
      const { px, py } = pointToPixel(point, series.eloKey, plot, minY, maxY, maxGameCount);
      return {
        point,
        px,
        py,
        dist: Math.hypot(chartX - px, chartY - py),
      };
    });

    let lineDist = pixels[0].dist;
    let nearest = pixels[0];

    if (pixels.length === 1) {
      lineDist = pixels[0].dist;
    } else {
      lineDist = Infinity;
      for (let i = 0; i < pixels.length - 1; i += 1) {
        const { dist, t } = distanceToSegment(
          chartX,
          chartY,
          pixels[i].px,
          pixels[i].py,
          pixels[i + 1].px,
          pixels[i + 1].py
        );
        if (dist < lineDist) {
          lineDist = dist;
          nearest = t < 0.5 ? pixels[i] : pixels[i + 1];
        }
      }
    }

    if (!best || lineDist < best.lineDist) {
      best = {
        series: series.id,
        point: nearest.point,
        elo: nearest.point[series.eloKey],
        lineDist,
        px: nearest.px,
        py: nearest.py,
      };
    }
  });

  if (!best || best.lineDist > maxDistance) return null;
  return best;
}

function resolveChartEvent(first, second) {
  if (first?.currentTarget?.getBoundingClientRect) return first;
  if (second?.currentTarget?.getBoundingClientRect) return second;
  return null;
}

function getChartPointer(reactEvent) {
  if (!reactEvent) return null;
  const target = reactEvent.currentTarget;
  if (!target?.getBoundingClientRect) return null;

  const touch = reactEvent.touches?.[0] || reactEvent.changedTouches?.[0];
  const clientX = touch ? touch.clientX : reactEvent.clientX;
  const clientY = touch ? touch.clientY : reactEvent.clientY;
  if (clientX == null || clientY == null) return null;

  const rect = target.getBoundingClientRect();
  return {
    chartX: clientX - rect.left,
    chartY: clientY - rect.top,
  };
}

function PlotAreaTracker({ onChange }) {
  const plotArea = usePlotArea();

  useEffect(() => {
    if (!plotArea) return;
    onChange(plotArea);
  }, [plotArea, onChange]);

  return null;
}

function LegendPill({ color, label }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-border-light bg-surface px-3 py-1 text-xs font-semibold text-text-primary shadow-brand-sm">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} aria-hidden />
      {label}
    </div>
  );
}

function ChartActiveDot({ color, cx, cy }) {
  if (cx == null || cy == null) return null;
  return (
    <>
      <circle cx={cx} cy={cy} r={6} fill={color} fillOpacity={0.12} />
      <circle
        cx={cx}
        cy={cy}
        r={2.5}
        fill={color}
        stroke="var(--color-surface)"
        strokeWidth={1.5}
      />
    </>
  );
}

const EloSelectionDetails = memo(function EloSelectionDetails({ selection, colors, compact = false }) {
  if (!selection?.point) return null;

  const isMixed = selection.series === 'mixed';
  const data = selection.point;
  const label = isMixed ? 'Mixed' : 'Levels';
  const color = isMixed ? colors.mixed : colors.levels;
  const elo = isMixed ? data.mixedElo : data.levelsElo;
  const change = isMixed ? data.mixedChange : data.levelsChange;

  const teamLine = [data.gameMatchup, data.gameScore].filter(Boolean).join(' · ');
  const contextLine = buildTooltipContextLine(data.sessionName, data.clubName);

  return (
    <div
      className={`rounded-xl border border-border-light bg-surface shadow-brand-md ${
        compact ? 'min-w-[10rem] p-3' : 'p-3'
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
        {data.dateLabel || data.date}
      </p>
      <p className="mt-1 text-sm font-semibold leading-snug text-text-primary">{contextLine}</p>
      {teamLine ? (
        <p className="mt-2 text-xs font-medium tabular-nums text-text-primary">{teamLine}</p>
      ) : null}
      <p className="mt-2 text-sm font-semibold tabular-nums text-text-primary">
        <span style={{ color }}>{label}</span>
        <span className="text-text-muted">: </span>
        <span>{elo}</span>
        {change !== 0 && (
          <span
            className={`ml-1.5 text-xs font-semibold tabular-nums ${
              change > 0 ? 'text-success-dark' : 'text-danger-dark'
            }`}
          >
            {change > 0 ? '+' : ''}
            {change}
          </span>
        )}
      </p>
    </div>
  );
});

/**
 * ELO history chart with gradient areas and sports-dashboard styling.
 */
function EloHistoryChart({ eloHistory, pairType, selectedPlayer: _selectedPlayer = null }) {
  const chartId = useId().replace(/:/g, '');
  const theme = getChartThemeColors();
  const colors = { mixed: theme.mixed, levels: theme.levels };
  const plotAreaRef = useRef(null);
  const [plotArea, setPlotArea] = useState(null);
  const [selection, setSelection] = useState(null);
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);

  const chartData = useMemo(() => buildChartDataFromEloHistory(eloHistory), [eloHistory]);

  const { minY, maxY, yAxisTicks, maxGameCount, latestMixed, latestLevels } = useMemo(() => {
    const allEloValues = (eloHistory || []).map((record) => record.new_value);
    const rawMin = allEloValues.length > 0 ? Math.min(...allEloValues) : 1200;
    const rawMax = allEloValues.length > 0 ? Math.max(...allEloValues) : 1200;
    const padding = 50;
    const minYCalc = Math.floor((rawMin - padding) / 100) * 100;
    const maxYCalc = Math.ceil((rawMax + padding) / 100) * 100;
    const ticks = [];
    for (let t = minYCalc; t <= maxYCalc; t += 100) ticks.push(t);
    if (ticks.length === 0) ticks.push(1100, 1200, 1300);
    const maxGc =
      chartData.length > 0 ? Math.max(...chartData.map((d) => d.gameCount || 0)) : 0;

    let mixed = null;
    let levels = null;
    for (let i = chartData.length - 1; i >= 0; i -= 1) {
      const point = chartData[i];
      if (mixed == null && point.mixedElo != null) mixed = point.mixedElo;
      if (levels == null && point.levelsElo != null) levels = point.levelsElo;
      if (mixed != null && levels != null) break;
    }

    return {
      minY: minYCalc,
      maxY: maxYCalc,
      yAxisTicks: ticks,
      maxGameCount: maxGc,
      latestMixed: mixed,
      latestLevels: levels,
    };
  }, [eloHistory, chartData]);

  const showMixed = pairType === 'all' || pairType === 'mixed';
  const showLevels = pairType === 'all' || pairType === 'levels';
  const showLegend = showMixed && showLevels;
  const policyMarkers = useMemo(
    () => getEloPolicyUpdateMarkers(chartData, { showMixed, showLevels }),
    [chartData, showMixed, showLevels]
  );

  const seriesList = useMemo(() => {
    const list = [];
    if (showMixed) {
      list.push({ id: 'mixed', eloKey: 'mixedElo', points: getSeriesPoints(chartData, 'mixedElo') });
    }
    if (showLevels) {
      list.push({ id: 'levels', eloKey: 'levelsElo', points: getSeriesPoints(chartData, 'levelsElo') });
    }
    return list;
  }, [chartData, showMixed, showLevels]);

  const xTickCount = useMemo(() => {
    if (maxGameCount <= 6) return maxGameCount || 1;
    if (maxGameCount <= 20) return 6;
    return 8;
  }, [maxGameCount]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(pointer: coarse)');
    const update = () => setIsCoarsePointer(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  useEffect(() => {
    setSelection(null);
  }, [eloHistory, pairType]);

  const handlePlotAreaChange = useCallback((nextPlotArea) => {
    plotAreaRef.current = nextPlotArea;
    setPlotArea((prev) => {
      if (
        prev &&
        prev.x === nextPlotArea.x &&
        prev.y === nextPlotArea.y &&
        prev.width === nextPlotArea.width &&
        prev.height === nextPlotArea.height
      ) {
        return prev;
      }
      return nextPlotArea;
    });
  }, []);

  const applyPointer = useCallback(
    (reactEvent, { clearIfFar }) => {
      const pointer = getChartPointer(reactEvent);
      const currentPlot = plotAreaRef.current;
      if (!pointer || !currentPlot) {
        if (clearIfFar) setSelection(null);
        return;
      }

      const next = pickNearestSeries({
        chartX: pointer.chartX,
        chartY: pointer.chartY,
        seriesList,
        plot: currentPlot,
        minY,
        maxY,
        maxGameCount,
        maxDistance: isCoarsePointer ? COARSE_HIT_DISTANCE : FINE_HIT_DISTANCE,
      });

      if (next) {
        setSelection(next);
        return;
      }
      if (clearIfFar) setSelection(null);
    },
    [seriesList, minY, maxY, maxGameCount, isCoarsePointer]
  );

  const handleMouseMove = useCallback(
    (first, second) => {
      applyPointer(resolveChartEvent(first, second), { clearIfFar: !isCoarsePointer });
    },
    [applyPointer, isCoarsePointer]
  );

  const handleClick = useCallback(
    (first, second) => {
      applyPointer(resolveChartEvent(first, second), { clearIfFar: true });
    },
    [applyPointer]
  );

  const handleMouseLeave = useCallback(() => {
    if (!isCoarsePointer) setSelection(null);
  }, [isCoarsePointer]);

  const mixedGradId = `mixedGrad-${chartId}`;
  const levelsGradId = `levelsGrad-${chartId}`;
  const selectedColor = selection?.series === 'mixed' ? colors.mixed : colors.levels;
  const tooltipLeft = selection && plotArea
    ? Math.min(Math.max(selection.px, 88), plotArea.x + plotArea.width - 8)
    : 0;
  const tooltipAbove = selection ? selection.py > 88 : true;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {showMixed && latestMixed != null && (
            <div className="rounded-full bg-female-light px-3 py-1 text-xs font-semibold tabular-nums text-female dark:bg-female-light/20">
              Mixed {Math.round(latestMixed)}
            </div>
          )}
          {showLevels && latestLevels != null && (
            <div className="rounded-full bg-primary-light px-3 py-1 text-xs font-semibold tabular-nums text-primary">
              Levels {Math.round(latestLevels)}
            </div>
          )}
        </div>
        {showLegend && (
          <div className="flex flex-wrap items-center gap-2">
            <LegendPill color={colors.mixed} label="Mixed ELO" />
            <LegendPill color={colors.levels} label="Levels ELO" />
          </div>
        )}
      </div>

      <div className="relative min-h-0 flex-1 select-none rounded-2xl border border-border-light bg-surface-subtle/70 p-2 pl-5 sm:p-3 sm:pl-6">
        <p className="pointer-events-none absolute left-1.5 top-1/2 z-10 -translate-y-1/2 -rotate-90 text-[10px] font-semibold uppercase tracking-wider text-text-muted sm:left-2">
          Rating
        </p>
        <div className="relative h-full w-full">
        <ResponsiveContainer width="100%" height="100%" debounce={80}>
          <ComposedChart
            data={chartData}
            margin={CHART_MARGIN}
            onMouseMove={handleMouseMove}
            onClick={handleClick}
            onTouchEnd={handleClick}
            onMouseLeave={handleMouseLeave}
          >
            <PlotAreaTracker onChange={handlePlotAreaChange} />
            <defs>
              <linearGradient id={mixedGradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.mixed} stopOpacity={0.28} />
                <stop offset="95%" stopColor={colors.mixed} stopOpacity={0} />
              </linearGradient>
              <linearGradient id={levelsGradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.levels} stopOpacity={0.22} />
                <stop offset="95%" stopColor={colors.levels} stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="4 6"
              vertical={false}
              stroke={theme.grid}
              strokeOpacity={0.85}
            />

            <XAxis
              type="number"
              dataKey="gameCount"
              domain={[1, maxGameCount || 1]}
              allowDecimals={false}
              tickCount={xTickCount}
              tick={{ fill: theme.textMuted, fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: theme.border, strokeWidth: 1 }}
              label={{
                value: 'Games played',
                position: 'insideBottom',
                offset: -2,
                fill: theme.textMuted,
                fontSize: 11,
                fontWeight: 600,
              }}
            />
            <YAxis
              domain={[minY, maxY]}
              ticks={yAxisTicks}
              allowDecimals={false}
              tick={{ fill: theme.textMuted, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={36}
            />

            <ReferenceLine
              y={1200}
              stroke={theme.textMuted}
              strokeDasharray="5 5"
              strokeOpacity={0.55}
              label={{
                value: 'Base 1200',
                position: 'insideTopRight',
                fill: theme.textMuted,
                fontSize: 10,
                fontWeight: 600,
              }}
            />

            {policyMarkers.map((marker) => (
              <ReferenceLine
                key={`elo-policy-${marker.seriesId}-${marker.gameCount}`}
                x={marker.gameCount}
                stroke={theme.textMuted}
                strokeDasharray="4 4"
                strokeOpacity={0.7}
                label={{
                  value: marker.label,
                  position: 'insideTopLeft',
                  fill: theme.textMuted,
                  fontSize: 10,
                  fontWeight: 600,
                }}
              />
            ))}

            {showMixed && (
              <>
                <Area
                  type="monotone"
                  dataKey="mixedElo"
                  stroke="none"
                  fill={`url(#${mixedGradId})`}
                  connectNulls
                  isAnimationActive={false}
                  tooltipType="none"
                />
                <Line
                  type="monotone"
                  dataKey="mixedElo"
                  name="Mixed ELO hit"
                  stroke="transparent"
                  strokeWidth={28}
                  dot={false}
                  activeDot={false}
                  connectNulls
                  isAnimationActive={false}
                  legendType="none"
                  tooltipType="none"
                />
                <Line
                  type="monotone"
                  dataKey="mixedElo"
                  name="Mixed ELO"
                  stroke={colors.mixed}
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                  connectNulls
                  isAnimationActive={false}
                  tooltipType="none"
                />
              </>
            )}

            {showLevels && (
              <>
                <Area
                  type="monotone"
                  dataKey="levelsElo"
                  stroke="none"
                  fill={`url(#${levelsGradId})`}
                  connectNulls
                  isAnimationActive={false}
                  tooltipType="none"
                />
                <Line
                  type="monotone"
                  dataKey="levelsElo"
                  name="Levels ELO hit"
                  stroke="transparent"
                  strokeWidth={28}
                  dot={false}
                  activeDot={false}
                  connectNulls
                  isAnimationActive={false}
                  legendType="none"
                  tooltipType="none"
                />
                <Line
                  type="monotone"
                  dataKey="levelsElo"
                  name="Levels ELO"
                  stroke={colors.levels}
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                  connectNulls
                  isAnimationActive={false}
                  tooltipType="none"
                />
              </>
            )}

            {selection && (
              <ReferenceDot
                x={selection.point.gameCount}
                y={selection.elo}
                r={3}
                ifOverflow="visible"
                isFront
                shape={(props) => <ChartActiveDot color={selectedColor} cx={props.cx} cy={props.cy} />}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>

        {selection && !isCoarsePointer && (
          <div
            className="pointer-events-none absolute z-20"
            style={{
              left: tooltipLeft,
              top: selection.py,
              transform: tooltipAbove ? 'translate(-50%, calc(-100% - 10px))' : 'translate(-50%, 12px)',
            }}
          >
            <EloSelectionDetails selection={selection} colors={colors} compact />
          </div>
        )}
        </div>
      </div>

      {isCoarsePointer && (
        <div className="mt-3" aria-live="polite">
          {selection ? (
            <EloSelectionDetails selection={selection} colors={colors} />
          ) : (
            <p className="text-center text-xs text-text-muted">Tap a line to see that game&apos;s rating</p>
          )}
        </div>
      )}
    </div>
  );
}

export {
  pickNearestSeries,
  buildChartDataFromEloHistory,
  getEloPolicyUpdateMarkers,
  formatGameMatchup,
  formatGameScore,
  buildTooltipContextLine,
  sessionIncludesClub,
};
export default memo(EloHistoryChart);
